import type { ImageCorners } from "./geo";

export interface DecodedFrame {
  /** data: URL — synchronous canvas.toDataURL(), see renderFrame.ts. */
  url: string;
  corners: ImageCorners;
  width: number;
  height: number;
}

const DEFAULT_CAP = 80;

/** Generic insertion-ordered LRU cache. Evicts oldest past cap on `set`.
 * Shared by the per-station/composite frame cache (below) and the mosaic
 * windowed-decode cache (mosaicDecoder.ts), which is keyed by (ts, level,
 * window-bucket) rather than "station:ts" but needs identical eviction
 * behavior — same struct, no reason to duplicate it. */
export class LruCache<T> {
  private map = new Map<string, T>();

  constructor(private cap = DEFAULT_CAP) {}

  get(key: string): T | undefined {
    return this.map.get(key);
  }

  has(key: string): boolean {
    return this.map.has(key);
  }

  set(key: string, value: T): void {
    this.map.delete(key); // re-insert to move to the "newest" end
    this.map.set(key, value);
    while (this.map.size > this.cap) {
      const oldestKey = this.map.keys().next().value;
      if (oldestKey === undefined) break;
      this.map.delete(oldestKey);
    }
  }
}

/** Cache of decoded per-station frames, keyed "STATION:ts". */
export class FrameCache extends LruCache<DecodedFrame> {}

export function frameCacheKey(station: string, ts: string): string {
  return `${station}:${ts}`;
}

/** LRU cache capped by TOTAL BYTES rather than item count — mosaicFrameCache.ts's
 * shared decoded+smoothed-grid cache (a decoded IFD0 grid is ~19.6MB; an
 * item-count cap like `LruCache`'s treats a 19.6MB entry and a 1.2MB one
 * identically, which is exactly wrong for caching decode results at
 * multiple LODs — see mosaicFrameCache.ts's doc comment for why this
 * exists instead of reusing `LruCache` with a bigger number). Same
 * insertion-ordered-Map eviction strategy as `LruCache`, just tracking a
 * running byte total and evicting oldest-first until back under budget
 * (never evicting the single most-recent entry even if it alone exceeds
 * budget — refusing to cache the CURRENT frame because it's big would be
 * worse than temporarily going over budget by one entry). */
export class ByteBudgetedLruCache<T> {
  private map = new Map<string, { value: T; bytes: number }>();
  private totalBytes = 0;

  constructor(private budgetBytes: number) {}

  get(key: string): T | undefined {
    const entry = this.map.get(key);
    if (!entry) return undefined;
    this.map.delete(key); // re-insert to move to the "newest" end
    this.map.set(key, entry);
    return entry.value;
  }

  has(key: string): boolean {
    return this.map.has(key);
  }

  /** `bytes` is the caller's own size estimate for `value` (mosaicFrameCache.ts
   * uses the underlying Uint8Array's `byteLength`) — this cache has no way
   * to introspect an arbitrary `T` itself. */
  set(key: string, value: T, bytes: number): void {
    const existing = this.map.get(key);
    if (existing) this.totalBytes -= existing.bytes;
    this.map.delete(key);
    this.map.set(key, { value, bytes });
    this.totalBytes += bytes;
    while (this.totalBytes > this.budgetBytes && this.map.size > 1) {
      const oldestKey = this.map.keys().next().value;
      if (oldestKey === undefined) break;
      const oldest = this.map.get(oldestKey);
      if (oldest) this.totalBytes -= oldest.bytes;
      this.map.delete(oldestKey);
    }
  }

  /** Current total bytes across all cached entries — test/debug visibility
   * into eviction behavior. */
  get currentBytes(): number {
    return this.totalBytes;
  }

  get size(): number {
    return this.map.size;
  }
}
